import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useModal } from "hooks/common/useModal";
import { useRole } from "hooks/common/useRole";
import { Avatar } from "components/Common/Avatar";
import { Text } from "components/Typography/Text";
import { Loader } from "components/Common/Loader";
import { Button } from "components/Interactive/Button";
import { EditGroupModal } from "../EditGroupModal";
import { GroupDetailsContent } from "content";
import { getStudentPlural } from "utils/content";
import { Route } from "constants/routes";
import { TGroup } from "types/api/groups";
import "./GroupHero.styles.scss";

type GroupHeroProps = {
  /**
   * Group data to display in `Hero` component.
   */
  group?: TGroup;
  /**
   * If `true` component will display loader with animation.
   *
   * @default false
   */
  loading?: boolean;
};

export const GroupHero: React.FC<GroupHeroProps> = (props) => {
  const { group, loading = false } = props;
  const { isUserTeacher, isUserAdmin } = useRole();
  const navigate = useNavigate();

  const {
    isModalOpen: isEditModalOpen,
    closeModal: closeEditModal,
    openModal: openEditModal,
  } = useModal(false);

  const studentsAmountLabel: string = useMemo(
    () => getStudentPlural(group?.memberIds.length),
    [group?.memberIds.length]
  );

  const editGroupClickHandler = () => {
    openEditModal();
  };

  const goToClassroomClickHandler = () => {
    if (group?.id) {
      navigate(Route.App.Study.GROUP_CLASSROOM.construct(group.id.toString()));
    }
  };

  return (
    <>
      <section className="nb-sub-group-details-hero">
        {loading ? (
          <Loader />
        ) : (
          <div className="nb-sub-group-details-hero-wrapper">
            <div className="nb-sub-group-details-hero-content">
              <Avatar bordered size="small" />
              <div className="nb-sub-group-details-hero-info">
                <Text
                  variant="body1"
                  bold
                  className="nb-sub-group-details-hero-title"
                >
                  {group?.name}
                </Text>
                <Text variant="body2">{studentsAmountLabel}</Text>
              </div>
            </div>
            <div className="nb-sub-group-details-hero-actions">
              {isUserTeacher && (
                <Button
                  variant="primary"
                  className="nb-sub-group-details-hero-button"
                  onClick={goToClassroomClickHandler}
                >
                  {GroupDetailsContent.Hero.Action.Button.MATERIALS}
                </Button>
              )}
              {isUserAdmin && (
                <Button
                  variant="secondary"
                  className="nb-sub-group-details-hero-button"
                  onClick={editGroupClickHandler}
                >
                  {GroupDetailsContent.Hero.Action.Button.ADD_STUDENTS}
                </Button>
              )}
            </div>
          </div>
        )}
      </section>
      <EditGroupModal
        group={group}
        open={isEditModalOpen}
        onClose={closeEditModal}
      />
    </>
  );
};
